import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, MapPin } from 'lucide-react';
import LanguageToggle from '../LanguageToggle';
import { Region } from '../../data/types/region-types';
import RegionNavigation from '../header/RegionNavigation';
import SpecialRegionsDropdown from '../header/SpecialRegionsDropdown';
import { useIsMobile } from '../../hooks/use-mobile';
import { 
  Drawer, 
  DrawerContent, 
  DrawerHeader, 
  DrawerTitle, 
  DrawerTrigger,
  DrawerFooter,
  DrawerClose
} from '@/components/ui/drawer';
import { Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/contexts/language';

interface HeaderProps {
  onPledgeClick: () => void;
  onPitchClick: () => void;
  regions: Region[];
  onRegionSelect: (regionId: string) => void;
  onCitySelect: (cityId: string) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  isChiapasActive?: boolean;
  isHidalgoActive?: boolean;
  onHomeClick: () => void;
  onMexicoClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  regions,
  onRegionSelect,
  onCitySelect,
  isChiapasActive,
  isHidalgoActive,
  onHomeClick,
  onMexicoClick
}) => {
  const { isMobile } = useIsMobile();
  const { t, language, toggleLanguage } = useLanguage();

  const MobileLanguageToggle = () => (
    <Drawer>
      <DrawerTrigger asChild>
        <Button 
          variant="ghost" 
          className="text-white hover:bg-white/20 px-3 py-2 flex items-center gap-2"
        >
          <Languages className="h-4 w-4" />
          {language === 'en' ? 'English' : 'Español'}
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[50vh]">
        <DrawerHeader>
          <DrawerTitle>{t('selectLanguage')}</DrawerTitle>
        </DrawerHeader>
        <div className="p-4 flex flex-col space-y-2">
          <Button 
            variant={language === 'en' ? "default" : "outline"}
            className="w-full justify-start"
            onClick={() => {
              if (language !== 'en') toggleLanguage();
            }}
          >
            🇺🇸 English
          </Button>
          <Button 
            variant={language === 'es' ? "default" : "outline"}
            className="w-full justify-start"
            onClick={() => {
              if (language !== 'es') toggleLanguage();
            }}
          >
            🇲🇽 Español
          </Button>
        </div>
        <DrawerFooter>
          <DrawerClose asChild>
            <Button variant="outline">{t('close')}</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );

  return (
    <header className="bg-jireh-primary p-2 md:p-4 z-40 shadow-md">
      <div className="container mx-auto flex flex-wrap justify-between items-center">
        <div className="flex items-center gap-2 md:gap-6 mb-2 md:mb-0 w-full md:w-auto">
          <Link to="/" className="cursor-pointer" onClick={onHomeClick}>
            <h1 className="text-2xl md:text-3xl font-jireh font-bold text-white tracking-wider hover:opacity-80 transition-opacity">
              JIREH
            </h1>
          </Link>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="default"
              className="text-white hover:bg-white/20 flex items-center gap-2"
              onClick={onHomeClick}
            >
              <Globe className="h-5 w-5" />
              {t('worldView') || "World View"}
            </Button>

            <Button
              variant="ghost"
              size="default"
              className="text-white hover:bg-white/20 flex items-center gap-2"
              onClick={onMexicoClick}
            >
              <MapPin className="h-5 w-5" />
              Mexico
            </Button>
          </div>
          
          <RegionNavigation
            onRegionSelect={onRegionSelect}
            isChiapasActive={isChiapasActive}
            isHidalgoActive={isHidalgoActive}
          />
        </div>
        
        <div className="flex-1 max-w-md mx-auto md:mx-4 w-full">
          <SpecialRegionsDropdown
            onCitySelect={onCitySelect}
            isChiapasActive={isChiapasActive}
            isHidalgoActive={isHidalgoActive}
          />
        </div>
        
        <div className="flex items-center justify-end gap-2 md:gap-4 w-full md:w-auto mt-2 md:mt-0">
          <div className="ml-2">
            {isMobile ? <MobileLanguageToggle /> : <LanguageToggle />}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
